// /app/frontend/src/components/configurator/ShadeVisualizer.jsx
// CSS-based shade preview component with preloaded scene buffering — no flash, no fabric-before-room.

import React, { useState, useMemo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { X, Maximize2, Printer, Mail, ChevronLeft, ChevronRight, Sparkles, SlidersHorizontal } from 'lucide-react';
import { ConfiguratorShadePreview, isRomanFabric } from './ConfiguratorShadePreview';
import { useRomanFold, foldLabel, liningLabel, useLiningHover, ROMAN_FOLDS, FOLD_INFO } from '../../lib/romanFold';
import { roomLabel } from '../../lib/roomTypes';
import { classifyFabric } from '../fabric/fabricLight';
import { AiSceneOpenness, regionsForRender, canComposite, absUrl } from './AiSceneOpenness';
import { AiRegenerateButton, ROOM_OPTIONS } from './AiRegenerateDialog';
import { RoomStylePicker as RoomStylePickerBase } from './RoomStylePicker';
import { trackRoomEvent } from '../../lib/roomEvents';
import { EmptyPreviewCard } from './EmptyPreviewCard';
import { sceneSrc, preloadScene } from '../../lib/sceneImages';

const setUrlRoom = (room) => {
  const url = new URL(window.location.href);
  if (room) url.searchParams.set('room', room); else url.searchParams.delete('room');
  window.history.replaceState(window.history.state, '', url);
};

const sceneLabel = (id) => ROOM_OPTIONS.find(o => o.value === id)?.label || roomLabel(id);

// Preload helper: decoded once per URL for the session (aspect used for overlay positioning).
const preloadImage = preloadScene;



const ShadeVisualizer = ({
  config,
  selectedColor,
  selectedCollection,
  selectedMaterial,
  productData,
  productType,
  currentVisualization,
  colorVisualizationImages = {},
  selectedOpenness,
  onVisualizationApproved,
  onFullscreenChange,
  onChooseFabric
}) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [viewMode, setViewMode] = useState('ai');
  const [userToggled, setUserToggled] = useState(false);
  const [activeRoomIdx, setActiveRoomIdx] = useState(0);
  const [roomPicked, setRoomPicked] = useState(false);
  const [roomsVersion, setRoomsVersion] = useState(0);

  // Notify parent when fullscreen state changes (to hide SharePreview bar etc.)
  React.useEffect(() => { onFullscreenChange?.(isFullscreen); }, [isFullscreen, onFullscreenChange]);


  // ---- Helpers ----
  const getImageUrl = (viz) => {
    if (!viz?.image_url) return null;
    if (viz.image_url.startsWith('http')) return viz.image_url;
    return `${process.env.REACT_APP_BACKEND_URL}${viz.image_url}`;
  };

  // ---- Fabric classification (memoized for stable identity) ----
  const light = useMemo(() => classifyFabric(
    selectedColor,
    selectedMaterial,
    { collection_name: selectedCollection?.name, fabric_type: selectedCollection?.fabric_type || config?.fabric_material, openness: selectedCollection?.openness },
  ), [selectedColor, selectedMaterial, selectedCollection?.name, selectedCollection?.fabric_type, selectedCollection?.openness, config?.fabric_material]);
  const isBlackout = light.mode === 'blackout';

  // ---- Roman fold scenes: hobbled / relaxed have their own AI rooms; flat uses room_visualizations ----
  const [romanFold, setRomanFold] = useRomanFold();
  const [foldRoomMissing, setFoldRoomMissing] = useState(null);  // room kept on fold switch that has no AI scene for that fold
  const roman = isRomanFabric(selectedCollection, selectedColor);
  const foldMap = roman && selectedColor && romanFold !== 'flat' ? (selectedColor.roman_fold_visualizations?.[romanFold] || {}) : null;
  const foldScenes = !!foldMap && Object.values(foldMap).some(Boolean);
  const foldFallback = !!foldMap && !foldScenes;
  // Roman: open on the room every fold has a scene for, so switching folds stays like-for-like
  const preferRoom = useMemo(() => {
    if (!roman) return null;
    const maps = ROMAN_FOLDS.map(([f]) => (f === 'flat' ? selectedColor?.room_visualizations : selectedColor?.roman_fold_visualizations?.[f]) || {})
      .filter(m => Object.values(m).some(Boolean));
    if (maps.length < 2) return null;
    const common = Object.keys(maps[0]).filter(r => maps.every(m => m[r]));
    return common.includes(selectedColor?.default_room) ? selectedColor.default_room : common[0] || null;
  }, [roman, selectedColor, roomsVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Per-color render URL ----
  const colorRenderRaw = foldScenes ? (foldMap[selectedColor?.default_room] || Object.values(foldMap).find(Boolean)) : selectedColor?.room_visualizations?.[selectedColor?.default_room] || selectedColor?.room_visualizations?.living_room || selectedColor?.room_visualization_url || selectedColor?.ai_visualization_url || null;
  const colorRenderUrl = colorRenderRaw ? (colorRenderRaw.startsWith('http') ? colorRenderRaw : `${process.env.REACT_APP_BACKEND_URL}${colorRenderRaw}`) : null;

  // ---- Room switcher rooms ----
  const availableRooms = useMemo(() => {
    const rooms = [];
    const vizMap = (foldScenes ? foldMap : selectedColor?.room_visualizations) || {};
    for (const [roomId, url] of Object.entries(vizMap)) {
      if (url) {
        rooms.push({
          id: roomId,
          label: sceneLabel(roomId),
          url: url.startsWith('http') ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`
        });
      }
    }
    if (!foldScenes && colorVisualizationImages?.length > 0) {
      for (const viz of colorVisualizationImages) {
        const vizUrl = getImageUrl(viz);
        if (vizUrl && !rooms.find(r => r.url === vizUrl)) {
          rooms.push({
            id: viz.room_type || viz.id || `viz-${rooms.length}`,
            label: viz.title || viz.room_type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Room',
            url: vizUrl
          });
        }
      }
    }
    const def = preferRoom || selectedColor?.default_room;
    return def ? [...rooms].sort((a, b) => (b.id === def) - (a.id === def)) : rooms;
  }, [selectedColor, colorVisualizationImages, roomsVersion, foldScenes, romanFold, preferRoom]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep the picked room when the fold (and so the room list) changes
  const lastRoomRef = useRef(null);
  useEffect(() => {
    const wanted = foldRoomMissing || lastRoomRef.current;
    const idx = wanted ? availableRooms.findIndex(r => r.id === wanted) : -1;
    setActiveRoomIdx(idx >= 0 ? idx : 0);
    setFoldRoomMissing(roman && wanted && idx < 0 && foldScenes ? wanted : null);
  }, [romanFold]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setFoldRoomMissing(null); }, [selectedColor?.id]);
  const activeRoomId = availableRooms[activeRoomIdx]?.id || null;
  useEffect(() => { lastRoomRef.current = activeRoomId; });

  // Reset room index when color changes
  useEffect(() => { setActiveRoomIdx(0); setRoomPicked(false); }, [selectedColor?.id]);
  useEffect(() => { setRoomPicked(false); }, [currentVisualization?.image_url]);
  // Shareable room look: ?room=<id> picks that room style (if this color has it)
  useEffect(() => {
    const wanted = new URLSearchParams(window.location.search).get('room');
    const idx = wanted ? availableRooms.findIndex(r => r.id === wanted) : -1;
    if (idx >= 0) { setActiveRoomIdx(idx); setRoomPicked(true); }
  }, [selectedColor?.id, availableRooms, currentVisualization?.image_url]);

  // ---- AI / simulator detection ----
  const hasAiViz = !!(
    (currentVisualization?.color_id === config.fabric_color && currentVisualization?.ai_generated) ||
    (config.fabric_color && (colorRenderUrl || availableRooms.length > 0))
  );
  const blackoutDefaultsToSim = isBlackout && config.fabric_color && !selectedColor?.render_opaque;
  const effectiveMode = (blackoutDefaultsToSim && !userToggled) ? 'simulator' : viewMode;

  // ===========================================================================
  //  TARGET — what we WANT to display (may require image preloading)
  // ===========================================================================
  const target = useMemo(() => {
    // No fabric picked in the current Type/collection yet → branded empty state (never the simulator)
    if (!selectedColor && (selectedCollection || !config.fabric_color)) {
      return { type: 'empty', url: null, keepPrev: !!selectedCollection };
    }
    // Simulator mode (user toggled)
    if (config.fabric_color && effectiveMode === 'simulator' && hasAiViz) {
      return { type: 'simulator', url: null };
    }

    // Priority 1: AI-generated visualization for exact color
    if (!roomPicked && !foldScenes && !preferRoom && currentVisualization?.color_id === config.fabric_color && currentVisualization?.ai_generated) {
      const imgUrl = getImageUrl(currentVisualization);
      const regions = regionsForRender(selectedColor, null, imgUrl);
      // Skip compositing when:
      // 1. In-memory flag from a fresh approve (skipComposite), OR
      // 2. No valid regions exist (shade_regions were cleared on approve)
      const skipComposite = !!currentVisualization.skipComposite;
      const composited = !skipComposite && canComposite(regions, light);
      const sceneUrl = composited ? absUrl(regions.clean_url) : imgUrl;
      return {
        type: 'ai-scene', url: sceneSrc(sceneUrl), fullUrl: sceneUrl,
        vizId: currentVisualization.id,
        regions: composited ? regions : null,
        title: currentVisualization.title || `Visualization for ${selectedColor?.name}`,
        color: selectedColor, light, selectedOpenness, isBlackout,
      };
    }

    // Priority 1b: render from Admin → Fabric Colors (room renders)
    if (config.fabric_color && (colorRenderUrl || availableRooms.length > 0)) {
      const activeRoom = availableRooms[activeRoomIdx] || availableRooms[0];
      const displayUrl = activeRoom?.url || colorRenderUrl;
      if (displayUrl) {
        const regions = foldScenes ? null : regionsForRender(selectedColor, activeRoom?.id, displayUrl);
        const composited = canComposite(regions, light);
        const sceneUrl = composited ? absUrl(regions.clean_url) : displayUrl;
        return {
          type: 'room-render', url: sceneSrc(sceneUrl), fullUrl: sceneUrl,
          room: activeRoom, roomIdx: activeRoomIdx, rooms: availableRooms,
          // No CSS overlay on non-composited images (prevents misaligned fabric flash).
          regions: composited ? regions : null,
          title: `${selectedColor?.name} - ${activeRoom?.label || 'Room'}`,
          color: selectedColor, light, selectedOpenness, isBlackout,
          colorId: config.fabric_color,
        };
      }
    }

    // Priority 3: Visualization matching this color (non-AI)
    if (currentVisualization && (
      currentVisualization.color_id === config.fabric_color ||
      currentVisualization.fabric_color === selectedColor?.name ||
      currentVisualization.color_name === selectedColor?.name
    )) {
      return {
        type: 'viz-match', url: sceneSrc(getImageUrl(currentVisualization)), fullUrl: getImageUrl(currentVisualization),
        title: currentVisualization.title || 'Product visualization',
      };
    }

    // Priority 4: Generic visualization
    if (currentVisualization) {
      return {
        type: 'viz-generic', url: sceneSrc(getImageUrl(currentVisualization)), fullUrl: getImageUrl(currentVisualization),
        title: currentVisualization.title || 'Product visualization',
      };
    }

    // Simulator fallback (no AI viz at all)
    if (config.fabric_color) {
      return { type: 'simulator-fallback', url: null };
    }

    // Empty state
    return { type: 'empty', url: null };
  }, [config.fabric_color, effectiveMode, hasAiViz, currentVisualization, selectedColor,
      colorRenderUrl, availableRooms, activeRoomIdx, roomPicked, light, selectedOpenness, isBlackout, foldScenes, preferRoom, selectedCollection]); // eslint-disable-line react-hooks/exhaustive-deps

  // ===========================================================================
  //  COMMITTED SCENE — what we are ACTUALLY displaying (preloaded & decoded)
  //  Old scene stays on screen until the new image is ready, then we swap.
  // ===========================================================================
  const [committed, setCommitted] = useState(() => target.url ? null : target);
  const targetRef = useRef(target);
  targetRef.current = target;

  // Signature: changes only when the image URL or scene type changes (not on overlay-only prop updates).
  const signature = `${target.type}|${target.url || ''}`;

  useEffect(() => {
    const t = targetRef.current;

    // Non-image scenes (simulator / empty) → commit immediately.
    // Switching collection clears the color: keep the last Scene on screen until a new color is picked.
    if (!t.url) {
      setCommitted(prev => (t.keepPrev && prev?.url ? { ...prev, stale: true } : t));
      return;
    }

    // Preload the target image, then commit atomically
    let cancelled = false;
    preloadImage(t.url).then(({ aspect, src }) => {
      if (!cancelled) {
        setCommitted({ ...targetRef.current, imgAspect: aspect, src });
      }
    });
    return () => { cancelled = true; };
  }, [signature]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Derive overlay props: use live props when committed scene is current, snapshot during transitions ----
  const scene = committed;
  const isCurrent = scene?.url && scene.url === target.url;
  const oColor    = isCurrent ? selectedColor      : scene?.color;
  const oLight    = isCurrent ? light               : scene?.light;
  const oOpenness = isCurrent ? selectedOpenness    : scene?.selectedOpenness;
  const oBlackout = isCurrent ? isBlackout          : scene?.isBlackout;
  const oRegions  = isCurrent ? (target.regions || scene?.regions) : scene?.regions;

  // ---- Image URL for fullscreen / print / email ----
  const committedUrl = scene?.fullUrl || scene?.url || null;
  const committedTitle = scene?.title || `Visualization for ${selectedColor?.name || 'shade'}`;

  // ===========================================================================
  //  SUB-COMPONENTS
  // ===========================================================================

  // ---- Fullscreen Modal ----
  const FullscreenModal = () => createPortal(
    <>
      {isFullscreen && committedUrl && (
        <div
          className="fixed inset-0 bg-black flex items-center justify-center"
          style={{ zIndex: 9999 }}
          onClick={() => { setIsFullscreen(false); setZoomLevel(1); }}
        >
          <button
            onClick={() => { setIsFullscreen(false); setZoomLevel(1); }}
            className="absolute top-4 right-4 flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors z-10 backdrop-blur-sm"
          >
            <X className="w-5 h-5 text-white" />
            <span className="text-white text-sm font-medium">Close</span>
          </button>
          <button
            onClick={() => { setIsFullscreen(false); setZoomLevel(1); }}
            className="absolute top-4 left-4 flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 rounded-full transition-colors z-10"
          >
            <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            <span className="text-white text-sm font-medium">Back to Configurator</span>
          </button>
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 z-10">
            <button onClick={(e) => { e.stopPropagation(); setZoomLevel(Math.max(0.5, zoomLevel - 0.25)); }} className="w-8 h-8 flex items-center justify-center text-white hover:bg-white/20 rounded-full transition-colors">−</button>
            <span className="text-white text-sm min-w-[60px] text-center">{Math.round(zoomLevel * 100)}%</span>
            <button onClick={(e) => { e.stopPropagation(); setZoomLevel(Math.min(3, zoomLevel + 0.25)); }} className="w-8 h-8 flex items-center justify-center text-white hover:bg-white/20 rounded-full transition-colors">+</button>
            <div className="w-px h-4 bg-white/30 mx-1" />
            <button onClick={(e) => { e.stopPropagation(); setZoomLevel(1); }} className="text-white text-xs hover:bg-white/20 px-2 py-1 rounded transition-colors">Reset</button>
          </div>
          <div className="absolute bottom-6 left-6 text-white/80 text-sm max-w-[40%] truncate bg-black/30 backdrop-blur-sm px-3 py-2 rounded-lg">
            {committedTitle}
          </div>
          <div className="absolute bottom-6 right-6 flex items-center gap-2 z-10">
            <button
              onClick={(e) => {
                e.stopPropagation();
                const printWindow = window.open('', '_blank');
                printWindow.document.write(`<!DOCTYPE html><html><head><title>Visualization - ${committedTitle}</title><style>@media print{body{margin:0;padding:20px;font-family:system-ui,sans-serif}.header{text-align:center;margin-bottom:20px}.header h1{font-size:24px;margin:0 0 10px 0}.header p{color:#666;margin:0}.image-container{text-align:center;margin:20px 0}.image-container img{max-width:100%;max-height:70vh}.details{margin-top:20px;padding:15px;background:#f5f5f5;border-radius:8px}.details h3{margin:0 0 10px 0;font-size:16px}.details-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px}.detail-item{font-size:12px}.detail-label{color:#666}.footer{margin-top:30px;text-align:center;font-size:10px;color:#999}}</style></head><body><div class="header"><h1>Apollo Shades</h1><p>Room Visualization</p></div><div class="image-container"><img src="${committedUrl}" alt="${committedTitle}" /></div><div class="details"><h3>Fabric Details</h3><div class="details-grid"><div class="detail-item"><span class="detail-label">Color:</span> ${selectedColor?.name || 'N/A'}</div><div class="detail-item"><span class="detail-label">Code:</span> ${selectedColor?.code || 'N/A'}</div><div class="detail-item"><span class="detail-label">Product:</span> ${productType?.replace(/-/g, ' ')?.replace(/\b\w/g, l => l.toUpperCase()) || 'Roller Shades'}</div><div class="detail-item"><span class="detail-label">Date:</span> ${new Date().toLocaleDateString()}</div></div></div><div class="footer"><p>Generated by Apollo Shades Configurator | www.apolloshades.com</p></div></body></html>`);
                printWindow.document.close();
                printWindow.onload = () => { printWindow.print(); };
              }}
              className="flex items-center gap-2 px-3 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors backdrop-blur-sm"
            >
              <Printer className="w-4 h-4 text-white" />
              <span className="text-white text-sm">Print</span>
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                const subject = encodeURIComponent(`Apollo Shades - ${committedTitle}`);
                const body = encodeURIComponent(`Hi,\n\nI'd like to share this room visualization from Apollo Shades:\n\nFabric: ${selectedColor?.name || 'N/A'}\nCode: ${selectedColor?.code || 'N/A'}\nProduct: ${productType?.replace(/-/g, ' ')?.replace(/\b\w/g, l => l.toUpperCase()) || 'Roller Shades'}\n\nView the visualization: ${committedUrl}\n\nBest regards`);
                window.open(`mailto:?subject=${subject}&body=${body}`);
              }}
              className="flex items-center gap-2 px-3 py-2 bg-amber-600 hover:bg-amber-700 rounded-lg transition-colors"
            >
              <Mail className="w-4 h-4 text-white" />
              <span className="text-white text-sm">Email</span>
            </button>
          </div>
          <div className="relative overflow-auto w-full h-full flex items-center justify-center p-8" onClick={(e) => e.stopPropagation()}>
            {/* Image + shade overlay scale together as a single unit */}
            <div
              className="relative inline-block"
              style={{ transform: `scale(${zoomLevel})`, lineHeight: 0 }}
            >
              <img
                src={committedUrl}
                alt={committedTitle}
                className="block max-w-none cursor-move"
                style={{ maxHeight: zoomLevel === 1 ? '90vh' : 'none', maxWidth: zoomLevel === 1 ? '90vw' : 'none' }}
                draggable={false}
              />
              {/* Shade overlay: renders fabric on detected window quads in fullscreen.
                  containerAspect is null → quads map directly to image % coords (no object-cover crop).
                  Requires full compositing (clean bg + polygons) — no partial overlays. */}
              {(scene?.type === 'ai-scene' || scene?.type === 'room-render') && oRegions && scene?.imgAspect && !oBlackout && (
                <AiSceneOpenness
                  regions={oRegions}
                  imgAspect={scene.imgAspect}
                  containerAspect={null}
                  color={oColor}
                  light={oLight}
                  openness={oOpenness}
                  show={true}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </>,
    document.body
  );

  // ---- Zoom overlay ----
  const StaleHint = () => scene?.stale ? (
    <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20 bg-white/90 backdrop-blur-sm text-neutral-800 text-xs font-medium px-3 py-1.5 rounded-full shadow-sm pointer-events-none whitespace-nowrap" data-testid="scene-pick-color-hint">
      Pick a color{(selectedMaterial?.name || selectedCollection?.name) ? ` in ${selectedMaterial?.name || selectedCollection?.name}` : ''} to update the Scene
    </div>
  ) : null;

  const ZoomOverlay = () => committedUrl ? (
    <div
      className="absolute inset-0 bg-black/0 hover:bg-black/10 transition-colors flex items-end justify-end opacity-0 hover:opacity-100 cursor-pointer p-3"
      onClick={() => setIsFullscreen(true)}
    >
      <div className="bg-white/90 hover:bg-white rounded-lg px-3 py-2 flex items-center gap-2 shadow-lg transition-colors">
        <Maximize2 className="w-4 h-4 text-neutral-700" />
        <span className="text-sm text-neutral-700 font-medium">View Larger</span>
      </div>
    </div>
  ) : null;

  // ---- Openness hint: angled AI scenes have no overlay, so point shoppers to the Simulator ----
  const OpennessHint = () => (light.mode === 'screen' && !oRegions && !oBlackout) ? (
    <button
      type="button"
      onClick={(e) => handleViewToggle(e, 'simulator')}
      className="absolute bottom-3 left-3 z-20 flex items-center gap-1 bg-white/90 hover:bg-white backdrop-blur-sm text-amber-700 text-[10px] px-2 py-0.5 rounded-full font-semibold border border-amber-200 shadow-sm transition-colors"
      data-testid="openness-sim-hint"
    >
      <SlidersHorizontal className="w-3 h-3" /> See openness in Simulator
    </button>
  ) : null;

  // ---- Fold-specific approvals update this colour's fold scene map in place ----
  const handleApproved = (approvalData) => {
    const f = approvalData?.fold;
    if (f && f !== 'flat') {
      const c = selectedColor;
      c.roman_fold_visualizations = { ...(c.roman_fold_visualizations || {}), [f]: { ...(c.roman_fold_visualizations?.[f] || {}), [approvalData.room_type]: approvalData.url } };
      setUrlRoom(approvalData.room_type);
      setRoomsVersion(v => v + 1);
      return;
    }
    if (onVisualizationApproved) onVisualizationApproved(approvalData); else window.location.reload();
  };

  // ---- Lining hover preview: tint the AI scene a little darker/brighter before the lining is chosen ----
  const liningHover = useLiningHover();
  const LINING_TINT = { unlined: 'bg-white/15', privacy: 'bg-black/15', blackout: 'bg-black/40', interlining: 'bg-amber-950/20' };
  const LiningPreview = () => (roman && liningHover && liningHover !== (config.lining || 'unlined')) ? (
    <>
      <div className={`absolute inset-0 z-10 pointer-events-none transition-colors duration-300 ${LINING_TINT[liningHover] || ''}`} data-testid="lining-hover-tint" />
      <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20 rounded-full bg-black/60 backdrop-blur-sm px-3 py-1 text-[11px] font-medium text-white pointer-events-none" data-testid="lining-hover-label">
        Previewing {liningLabel(liningHover)} lining
      </div>
    </>
  ) : null;

  // ---- Phones: swipe left/right on a Roman AI scene to flip through fold styles ----
  const swipeStart = useRef(null);
  const [swiped, setSwiped] = useState(false);
  const [swipeDir, setSwipeDir] = useState(0); // -1 = swiped left (next), 1 = swiped right (prev); 0 = no slide
  const foldSwipe = roman ? {
    onTouchStart: (e) => { const t = e.touches[0]; swipeStart.current = { x: t.clientX, y: t.clientY }; },
    onTouchEnd: (e) => {
      const s0 = swipeStart.current; swipeStart.current = null;
      if (!s0) return;
      const t = e.changedTouches[0]; const dx = t.clientX - s0.x; const dy = t.clientY - s0.y;
      if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
      const ids = ROMAN_FOLDS.map(([id]) => id);
      const i = Math.max(0, ids.indexOf(romanFold));
      setSwipeDir(dx < 0 ? -1 : 1);
      setRomanFold(ids[(i + (dx < 0 ? 1 : -1) + ids.length) % ids.length]);
      setSwiped(true);
    },
  } : {};

  // ---- Roman fold label + fold-style picker on AI scenes (synced with Simulator, step and Compare) ----
  const FoldBadge = () => roman ? (
    <>
      <div className="absolute top-12 sm:top-auto sm:bottom-16 right-2 sm:right-3 z-20 flex flex-col gap-0.5 bg-white/90 backdrop-blur-sm rounded-md p-1 shadow-sm min-w-[5.5rem]" data-testid="ai-fold-style">
        <div className="px-2 pt-0.5 pb-1 text-[9px] font-semibold uppercase tracking-wide text-neutral-500 border-b border-neutral-200 mb-0.5" data-testid="ai-fold-badge">
          {`${foldLabel(romanFold)} fold`}
        </div>
        {ROMAN_FOLDS.map(([id, label]) => (
          <button key={id} type="button" onClick={() => { setSwipeDir(0); setRomanFold(id); }} title={FOLD_INFO[id]} data-testid={`ai-fold-${id}`}
            className={`px-2 py-0.5 rounded text-[10px] font-medium text-left transition-colors ${romanFold === id ? 'bg-neutral-900 text-white' : 'text-neutral-600 hover:bg-neutral-100'}`}>{label}</button>
        ))}
      </div>
      {!swiped && (
        <div className="sm:hidden absolute top-[13.25rem] right-2 z-20 flex items-center gap-1 rounded-full bg-black/55 backdrop-blur-sm px-2 py-0.5 text-[9px] font-medium text-white pointer-events-none" data-testid="ai-fold-swipe-hint">
          <ChevronLeft className="w-3 h-3" />Swipe for folds<ChevronRight className="w-3 h-3" />
        </div>
      )}
    </>
  ) : null;

  // ---- Scene Style Picker (admin can set default / delete; mutates selectedColor so it sticks this session) ----
  const handleRoomChanged = (action, room, data) => {
    const c = selectedColor;
    const f = action === 'restore' ? data?.fold : (foldScenes ? romanFold : null);
    if (f) {
      const map = { ...(c.roman_fold_visualizations?.[f] || {}) };
      if (action === 'restore') map[room.id] = data.url; else delete map[room.id];
      c.roman_fold_visualizations = { ...(c.roman_fold_visualizations || {}), [f]: map };
    } else if (action === 'restore') {
      c.room_visualizations = { ...(c.room_visualizations || {}), [room.id]: data.url };
      if (data.default_room) c.default_room = data.default_room;
    } else if (action === 'delete') {
      delete c.room_visualizations?.[room.id];
      delete c.shade_regions?.[room.id];
      if (c.default_room === room.id) delete c.default_room;
    } else {
      c.default_room = room.id;
      c.room_visualization_url = c.room_visualizations[room.id];
    }
    setUrlRoom(null);
    setRoomsVersion(v => v + 1);
    setActiveRoomIdx(0);
    setRoomPicked(true);
  };
  const RoomStylePicker = ({ activeIdx }) => (
    <RoomStylePickerBase
      rooms={availableRooms}
      activeIdx={activeIdx}
      defaultRoom={selectedColor?.default_room}
      colorId={selectedColor?.id}
      onPick={(idx) => { setActiveRoomIdx(idx); setRoomPicked(true); setFoldRoomMissing(null); setUrlRoom(availableRooms[idx]?.id); trackRoomEvent('pick', selectedColor?.id, availableRooms[idx]?.id, productType); }}
      onChanged={handleRoomChanged}
      fold={foldScenes ? romanFold : null}
    />
  );

  // ---- View mode toggle ----
  const handleViewToggle = (e, mode) => {
    e.preventDefault();
    e.stopPropagation();
    setViewMode(mode);
    setUserToggled(true);
  };

  const ViewModeToggle = () => (
    <div
      className="absolute bottom-20 left-1/2 -translate-x-1/2 z-20 flex bg-black/60 backdrop-blur-sm rounded-lg p-0.5"
      data-testid="view-mode-toggle"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <button type="button" onClick={(e) => handleViewToggle(e, 'ai')}
        className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium whitespace-nowrap transition-all ${effectiveMode === 'ai' ? 'bg-white text-neutral-900 shadow-sm' : 'text-white/70 hover:text-white'}`}
        data-testid="view-mode-ai"
      >
        <Sparkles className="w-3 h-3" />Scene
      </button>
      <button type="button" onClick={(e) => handleViewToggle(e, 'simulator')}
        className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium whitespace-nowrap transition-all ${effectiveMode === 'simulator' ? 'bg-white text-neutral-900 shadow-sm' : 'text-white/70 hover:text-white'}`}
        data-testid="view-mode-simulator"
      >
        <SlidersHorizontal className="w-3 h-3" />Simulator
      </button>
    </div>
  );

  // ===========================================================================
  //  RENDER — based on committed scene type
  // ===========================================================================

  // Initial state before any scene commits (only if first fabric has an image URL)
  if (!scene) {
    return (
      <div className="relative" style={{ aspectRatio: '3/4' }} data-testid="shade-loading-state">
        <div className="w-full h-full bg-neutral-100 animate-pulse rounded-lg" />
      </div>
    );
  }

  // ---- AI Scene for a roman fold with no scene yet (for this room): stay in AI Scene, say so, preview via Simulator ----
  if (roman && selectedColor && effectiveMode === 'ai' && (foldFallback || foldRoomMissing)) {
    const roomName = sceneLabel(foldRoomMissing || availableRooms[activeRoomIdx]?.id || '');
    return (
      <div className="relative" data-testid="ai-fold-fallback">
        <div className="absolute top-3 left-3 right-28 z-30 bg-amber-50/95 border border-amber-300 text-amber-800 text-[11px] leading-snug px-3 py-1 rounded-lg font-semibold shadow-sm" data-testid="ai-fold-fallback-note">
          No {foldLabel(romanFold)} Scene{foldRoomMissing ? ` for ${roomName}` : ''} yet · Simulator preview
        </div>
        <ConfiguratorShadePreview toggle={<ViewModeToggle />} selectedColor={selectedColor} selectedCollection={selectedCollection} selectedMaterial={selectedMaterial} config={config} />
        {foldRoomMissing && availableRooms.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5" data-testid="ai-fold-fallback-rooms">
            {availableRooms.map((r, idx) => (
              <button key={r.id} type="button" onClick={() => { setActiveRoomIdx(idx); setRoomPicked(true); setFoldRoomMissing(null); setUrlRoom(r.id); }}
                className="rounded-full border border-neutral-300 bg-white px-3 py-1 text-xs font-medium text-neutral-700 hover:border-neutral-900" data-testid={`ai-fold-fallback-room-${r.id}`}>
                View {foldLabel(romanFold)} Scene · {r.label}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ---- Simulator mode (user toggled to it) ----
  if (scene.type === 'simulator') {
    return (
      <div className="relative" data-testid="shade-simulator-view">
        <ConfiguratorShadePreview
          toggle={<ViewModeToggle />}
          selectedColor={selectedColor}
          selectedCollection={selectedCollection}
          selectedMaterial={selectedMaterial}
          config={config}
        />
      </div>
    );
  }

  // ---- AI Scene (Priority 1) ----
  if (scene.type === 'ai-scene') {
    return (
      <>
        <FullscreenModal />
        <div
          {...foldSwipe}
          className="relative group overflow-hidden"
          style={{ aspectRatio: '3/4' }}
          data-testid="ai-scene-visualization"
          data-fullscreen={isFullscreen ? 'true' : undefined}
        >
          {!isFullscreen && <ViewModeToggle />}
          <AiRegenerateButton
            selectedColor={selectedColor}
            currentVisualization={currentVisualization}
            fold={roman ? romanFold : null}
            onVisualizationUpdated={handleApproved}
          />
          <FoldBadge />
          <LiningPreview />
          <motion.img
            key={scene.url}
            initial={swipeDir ? { x: `${-swipeDir * 35}%`, opacity: 0.3 } : false}
            animate={{ x: 0, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 260, damping: 30 }}
            src={scene.src || scene.url}
            alt={scene.title}
            className="w-full h-full object-cover"
            onError={() => { setViewMode('simulator'); setUserToggled(true); }}
          />
          {/* Openness overlay — only renders when imgAspect is known (from preload) */}
          <AiSceneOpenness
            regions={oRegions}
            imgAspect={scene.imgAspect}
            containerAspect={4 / 3}
            color={oColor}
            light={oLight}
            openness={oOpenness}
            show={!oBlackout}
          />
          {oOpenness && oRegions && !oBlackout && (
            <div className="absolute top-2 right-2 bg-white/90 backdrop-blur-sm text-amber-700 text-[10px] px-2 py-0.5 rounded-full font-semibold border border-amber-200" data-testid="openness-badge">
              {oOpenness} openness
            </div>
          )}
          <ZoomOverlay />
          <StaleHint />
          <OpennessHint />
        </div>
        <RoomStylePicker activeIdx={availableRooms.findIndex(r => r.url === getImageUrl(currentVisualization))} />
      </>
    );
  }

  // ---- Room Render (Priority 1b) ----
  if (scene.type === 'room-render') {
    // Thumbnails use current state for immediate feedback; main image uses committed URL.
    const thumbRooms = availableRooms;
    const thumbIdx = activeRoomIdx;

    return (
      <>
        <FullscreenModal />
        <div className="relative group" data-testid="color-render-visualization">
          <div {...foldSwipe} className="relative overflow-hidden" style={{ aspectRatio: '3/4' }} data-fullscreen={isFullscreen ? 'true' : undefined}>
            {!isFullscreen && <ViewModeToggle />}
            <AiRegenerateButton
              selectedColor={selectedColor}
              currentVisualization={currentVisualization}
              fold={roman ? romanFold : null}
              onVisualizationUpdated={handleApproved}
            />
            <FoldBadge />
            <LiningPreview />
            <motion.img
              key={scene.url}
              initial={swipeDir ? { x: `${-swipeDir * 35}%`, opacity: 0.3 } : false}
              animate={{ x: 0, opacity: 1 }}
              transition={{ type: 'spring', stiffness: 260, damping: 30 }}
              src={scene.src || scene.url}
              alt={scene.title}
              className="w-full h-full object-cover"
              onError={() => { setViewMode('simulator'); setUserToggled(true); }}
            />
            <AiSceneOpenness
              regions={oRegions}
              imgAspect={scene.imgAspect}
              containerAspect={4 / 3}
              color={oColor}
              light={oLight}
              openness={oOpenness}
              show={!oBlackout}
            />
            <ZoomOverlay />
            <StaleHint />
            <OpennessHint />
            {oOpenness && oRegions && !oBlackout && (
              <div className="absolute top-2 right-2 bg-white/90 backdrop-blur-sm text-amber-700 text-[10px] px-2 py-0.5 rounded-full font-semibold border border-amber-200" data-testid="openness-badge">
                {oOpenness} openness
              </div>
            )}
            {/* Room type label */}
            {scene.room && (
              <div className="absolute top-2 left-2 bg-black/50 backdrop-blur-sm text-white text-[10px] px-2 py-0.5 rounded-full">
                {scene.room.label}
              </div>
            )}
            {/* Arrow navigation */}
            {thumbRooms.length > 1 && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); setActiveRoomIdx((thumbIdx - 1 + thumbRooms.length) % thumbRooms.length); }}
                  className="absolute left-1.5 top-1/2 -translate-y-1/2 w-7 h-7 bg-black/40 hover:bg-black/60 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid="room-switch-prev"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); setActiveRoomIdx((thumbIdx + 1) % thumbRooms.length); }}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 w-7 h-7 bg-black/40 hover:bg-black/60 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid="room-switch-next"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </>
            )}
          </div>
          <RoomStylePicker activeIdx={thumbIdx} />
        </div>
      </>
    );
  }

  // ---- Visualization match or generic (Priority 3 / 4) ----
  if (scene.type === 'viz-match' || scene.type === 'viz-generic') {
    return (
      <>
        <FullscreenModal />
        <div className="relative group" style={{ aspectRatio: '3/4' }}>
          <img
            src={scene.src || scene.url}
            alt={scene.title}
            className="w-full h-full object-cover"
          />
          <ZoomOverlay />
          <StaleHint />
        </div>
      </>
    );
  }

  // ---- Simulator fallback (no AI viz exists for this color) ----
  if (scene.type === 'simulator-fallback') {
    return (
      <div className="relative" data-testid="shade-simulator-view">
        <ConfiguratorShadePreview
          toggle={hasAiViz ? <ViewModeToggle /> : null}
          selectedColor={selectedColor}
          selectedCollection={selectedCollection}
          selectedMaterial={selectedMaterial}
          config={config}
        />
      </div>
    );
  }

  // ---- Empty state (no fabric selected) ----
  return (
    <div className="relative" style={{ aspectRatio: '3/4' }} data-testid="css-shade-simulator">
      <img
        src="https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?w=800&q=90"
        alt="Modern living room"
        className="w-full h-full object-cover"
      />
      <div className="absolute inset-0 flex items-center justify-center bg-black/30" data-testid="preview-empty-state">
        <EmptyPreviewCard onChooseFabric={onChooseFabric} typeLabel={selectedCollection ? [selectedCollection.name, selectedMaterial?.name].filter(Boolean).join(' · ') : null}
          colorCount={selectedMaterial?.colors?.length || (selectedCollection?.materials?.length ? selectedCollection.materials.reduce((n, m) => n + (m.colors?.length || 0), 0) : selectedCollection?.colors?.length) || 0} />
      </div>
      <div className="absolute inset-0 pointer-events-none" style={{
        background: 'linear-gradient(135deg, rgba(255,248,240,0.1) 0%, transparent 60%)'
      }} />
    </div>
  );
};

export default ShadeVisualizer;
